import { useCallback, useMemo, useState } from 'react';
import { formatSort, toggleSort, type SortKey } from '@forecastify/shared';
import { usePages, type Pages } from './use-pages';
import {
  rememberedString,
  rememberedStrings,
  useListMemory,
  useRememberEffect,
  type Remembered,
} from './list-memory';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Search, filters, sort and the page — one state, one query string.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS EXISTS SO THAT NOTHING CAN CHANGE A FILTER WITHOUT RESETTING THE PAGE.** That rule
 * is `state-rules.md` §6's most easily missed one — an offset into a set a filter has just shortened
 * lands past its end, and an empty page reads as *"nothing matches"* rather than as a mistake — and
 * every list screen had its own copy of it, each of which had to remember. Here it is one function
 * that no caller can route around: `setFilter`, `setSearch` and `onSortChange` all reset.
 *
 * ⚠️ **The sort resets the page too, and that is not obvious.** Re-sorting does not change WHICH
 * rows match, so page four still exists — but it now holds entirely different rows, and staying on
 * it means the screen jumps somewhere nobody asked to be. Every list in every product returns to
 * the top on a sort.
 *
 * ⚠️⚠️ **THE FILTER VALUES ARE STRINGS, INCLUDING THE BOOLEANS, BECAUSE A QUERY STRING IS.** `''`
 * means *not filtered* and is dropped from the request — which is what makes *"tracks time: no"*
 * expressible at all: `'false'` is a filter and `''` is its absence, where a JavaScript boolean
 * would have to smuggle the third state through `undefined`.
 */

export interface ListQuery {
  readonly search: string;
  readonly setSearch: (value: string) => void;

  /** ⚠️ `''` is *not filtered* and is not sent. */
  readonly filters: Readonly<Record<string, string>>;
  readonly setFilter: (key: string, value: string) => void;
  /**
   * Several at once, as a patch.
   *
   * ⚠️⚠️ **NOT `setFilter` CALLED IN A LOOP, AND REACT IS THE REASON.** Each call would read the
   * same `filters` from its own closure, so the last one would win and the rest would vanish — the
   * classic stale-closure bug, and here it would silently drop two of the three filters a saved
   * view sets.
   */
  readonly setFilters: (patch: Readonly<Record<string, string>>) => void;
  /** ⚠️ Clears every filter AND the search — the toolbar's *"Szűrők törlése"*. */
  readonly clearFilters: () => void;
  /**
   * 3 Nov 2026 · a preset or saved-view click — `setFilters`, and the result OVERWRITES what the
   * list remembers even on a visit an address filtered (`list-memory.ts` rule 2).
   */
  readonly applyPreset: (patch: Readonly<Record<string, string>>) => void;

  readonly sort: readonly SortKey[];
  /** ⚠️ Takes the whole next state, as `DataList` reports it. */
  readonly onSortChange: (next: readonly SortKey[]) => void;
  /** ⚠️ Applies the cycle from `@forecastify/shared` — for a caller that has only a column key. */
  readonly toggle: (key: string) => void;

  readonly pages: Pages;

  /**
   * The request's query string, search and filters and sort and offset together.
   *
   * ⚠️ **`limit` is NOT here.** The server's default is the contract's own (50), and a screen that
   * pinned a number would be the one place the default could not be changed.
   */
  readonly params: URLSearchParams;
}

export function useListQuery(
  initialFilters: Readonly<Record<string, string>> = {},
  /**
   * Filter keys that are the SCREEN'S state and never reach the server.
   *
   * ⚠️ **The saved view is the case this exists for.** *Aktív* is not a parameter the API knows; it
   * is three filters wearing one control, and the screen sets those alongside it. Keeping the
   * control's own value in the same map is what makes *"which view is selected"* have one home —
   * but sending it would be sending a parameter nothing reads.
   */
  local: readonly string[] = [],
  /** Rows per page — the app's default unless a list has its own, like a history's 25. */
  limit?: number,
  /**
   * 3 Nov 2026 · `persistKey` — remember search, filters and sort in the browser under this list
   * name (`list-memory.ts`). `transient` — the address carried its own filter this visit, so
   * nothing is restored and nothing saved until a preset or a clear.
   */
  memoryOptions: { readonly persistKey?: string; readonly transient?: boolean } = {},
): ListQuery {
  const pages = usePages(limit);
  const memory = useListMemory(memoryOptions.persistKey, memoryOptions.transient === true);
  const [search, setSearchState] = useState(() => rememberedString(memory.restored, 'search', ''));
  const [filters, setFiltersState] = useState<Readonly<Record<string, string>>>(() =>
    rememberedStrings(memory.restored, 'filters', initialFilters),
  );
  const [sort, setSortState] = useState<readonly SortKey[]>(() => sortOf(memory.restored));
  useRememberEffect(memory, { search, filters, sort });

  const { reset } = pages;

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      reset();
    },
    [reset],
  );

  const setFilter = useCallback(
    (key: string, value: string) => {
      setFiltersState((current) => ({ ...current, [key]: value }));
      reset();
    },
    [reset],
  );

  const setFilters = useCallback(
    (patch: Readonly<Record<string, string>>) => {
      setFiltersState((current) => ({ ...current, ...patch }));
      reset();
    },
    [reset],
  );

  const clearFilters = useCallback(() => {
    /* ⚠️ Back to what the screen STARTED with, not to `{}`: a screen that opens filtered — the
       time log's current week, a project's own tasks — is not clearing to *everything ever*. */
    setFiltersState(initialFilters);
    setSearchState('');
    memory.clear();
    reset();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `initialFilters` is an object
       literal at almost every call site, so depending on it would rebuild this every render and
       make it useless as a dependency itself. The value it closes over is the first one. */
  }, [reset, memory]);

  const applyPreset = useCallback(
    (patch: Readonly<Record<string, string>>) => {
      memory.overwrite({});
      setFilters(patch);
    },
    [memory, setFilters],
  );

  const onSortChange = useCallback(
    (next: readonly SortKey[]) => {
      setSortState(next);
      reset();
    },
    [reset],
  );

  const toggle = useCallback(
    (key: string) => {
      setSortState((current) => toggleSort(current, key));
      reset();
    },
    [reset],
  );

  const params = useMemo(() => {
    const query = new URLSearchParams();
    if (search.trim() !== '') query.set('search', search.trim());
    for (const [key, value] of Object.entries(filters)) {
      /* ⚠️ A local key is the screen's own state — see the `local` parameter. */
      if (value !== '' && !local.includes(key)) query.set(key, value);
    }
    const encoded = formatSort(sort);
    if (encoded !== undefined) query.set('sort', encoded);
    if (pages.offset > 0) query.set('offset', String(pages.offset));
    return query;
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `local` is a literal array at every
       call site, so depending on it would rebuild this every render. Its value is the first one. */
  }, [search, filters, sort, pages.offset]);

  return useMemo(
    () => ({
      search,
      setSearch,
      filters,
      setFilter,
      setFilters,
      clearFilters,
      applyPreset,
      sort,
      onSortChange,
      toggle,
      pages,
      params,
    }),
    [
      search,
      setSearch,
      filters,
      setFilter,
      setFilters,
      clearFilters,
      applyPreset,
      sort,
      onSortChange,
      toggle,
      pages,
      params,
    ],
  );
}

/** A remembered sort, item by item — anything malformed is dropped rather than sent. */
function sortOf(stored: Remembered | null): readonly SortKey[] {
  const value = stored?.sort;
  if (!Array.isArray(value)) return [];
  return value.filter(
    (one): one is SortKey =>
      typeof one === 'object' &&
      one !== null &&
      typeof (one as { key?: unknown }).key === 'string' &&
      ((one as { direction?: unknown }).direction === 'asc' ||
        (one as { direction?: unknown }).direction === 'desc'),
  );
}
